import React, { useEffect, useState } from 'react'
import { Field as FormikField, connect } from 'formik'
import { Field, Error } from '@/ui'
import { useTranslation } from 'react-i18next'
import styles from '@/entity/contracts/ui/form/ContractForm.module.scss'
import { contractsThunks, formFieldNames } from '@/entity'
import { useDispatch, useSelector } from 'react-redux'
import { getFromLocalStorage } from '@/shared'
import { formatDate } from '@/utils'

/**
 * Компонент ContractDuration - управляет формой для оформления трудовых договоров с иностранной рабочей силой
 *
 * Основные функции:
 * - Выбор основания привлечения нерезидентов (иерархический справочник)
 * - Управление полями разрешений (активируются по условию)
 * - Трёхуровневая система образования (образование -> специальность -> квалификация)
 * - Автоматическая валидация и установка сроков действия договоров
 * - Фильтрация разрешений по типу резидентства (TI/IRS)
 */
export const ContractDuration = connect(
	({
		nonresidentAttractingBasis = [], // Иерархический справочник оснований привлечения нерезидентов
		immigrantType, // Тип иммигранта (влияет на срок действия договора)
		isEdit, // Флаг режима редактирования
		eContract, // Флаг электронного договора
		setIsPermitError, // Функция установки ошибки разрешения
		isPermitError, // Текущая ошибка разрешения
		formik: { setFieldValue, setFieldTouched, setFieldError, values, errors },
		nonresidentEducation, // Иерархический справочник образования нерезидентов
		isFormDirty, // Флаг изменения формы
	}) => {
		const dispatch = useDispatch()
		const irsPermissionList = useSelector((state) => state.contracts.irsPermissionList)
		const { t } = useTranslation()

		// Состояния компонента
		const [withPermission, setWithPermission] = useState(false) // Требуется ли разрешение для выбранного основания
		const [childsValue1, setChildsValue1] = useState([]) // Дочерние элементы для первого уровня образования (специальности)
		const [childsValue2, setChildsValue2] = useState([]) // Дочерние элементы для второго уровня образования (квалификации)
		const [filteredNumberPermissionsList, setFilteredNumberPermissionsList] = useState([]) // Отфильтрованный список разрешений
		const [loadCalendar, setLoadCalendar] = useState(true) // Флаг для перезагрузки календаря

		const parsedActiveCompany = getFromLocalStorage('activeCompany')
		const residentType = parsedActiveCompany?.flEmployer ? 'TI' : 'IRS' // Тип резидентства: TI (трудовой иммигрант) или IRS (инвестиционный статус)

		// Zhanadil - получаем по бину работодателя - пермишены для найма
		useEffect(() => {
			dispatch(contractsThunks.getEmployerPermissionByBin(parsedActiveCompany?.bin))
		}, [])

		// Обработка изменений первого уровня образования (образование -> специальность)
		// При выборе образования загружаются соответствующие специальности
		useEffect(() => {
			const childs = nonresidentEducation.filter(
				(item) =>
					item.parentId === nonresidentEducation.find((i) => i.code === values.foreignLaborInfo.codes.value1)?.id
			)
			setChildsValue1(childs)
			// Сброс зависимых полей если выбранные значения не найдены в новом списке
			if (childs.findIndex((c) => c.code === values.foreignLaborInfo.codes.value2) === -1) {
				setFieldValue('foreignLaborInfo.codes.value2', null)
				setFieldValue('foreignLaborInfo.codes.value3', null)
			}
			// Если нет дочерних элементов, устанавливаем текущее значение как финальное
			if (!childs.length) {
				setFieldValue('foreignLaborInfo.dnonResidentEducationCode', values.foreignLaborInfo.codes.value1)
			}
		}, [values.foreignLaborInfo.codes.value1])

		// Обработка изменений второго уровня образования (специальность -> квалификация)
		// При выборе специальности загружаются соответствующие квалификации
		useEffect(() => {
			if (!values.foreignLaborInfo.codes.value2) {
				setChildsValue2([])
				setFieldValue('foreignLaborInfo.codes.value3', null)
				return
			}

			const childs = nonresidentEducation.filter(
				(item) =>
					item.parentId === nonresidentEducation.find((i) => i.code === values.foreignLaborInfo.codes.value2)?.id
			)
			setChildsValue2(childs)
			// Сброс третьего уровня если выбранное значение не найдено в новом списке
			if (childs.findIndex((c) => c.code === values.foreignLaborInfo.codes.value3) === -1) {
				setFieldValue('foreignLaborInfo.codes.value3', null)
			}
			// Если нет дочерних элементов, устанавливаем текущее значение как финальное
			if (!childs.length) {
				setFieldValue('foreignLaborInfo.dnonResidentEducationCode', values.foreignLaborInfo.codes.value2)
			}
		}, [values.foreignLaborInfo.codes.value2])

		// Обработка изменений третьего уровня образования (финальная квалификация)
		// Устанавливает выбранную квалификацию как итоговое значение образования
		useEffect(() => {
			if (values.foreignLaborInfo.codes.value3) {
				setFieldValue('foreignLaborInfo.dnonResidentEducationCode', values.foreignLaborInfo.codes.value3)
			}
		}, [values.foreignLaborInfo.codes.value3])

		// Обработка изменений основания привлечения нерезидентов
		// Логика работы с иерархическим справочником оснований:
		// - parentId = null: родительские категории (основные виды оснований)
		// - parentId = 1: дочерние элементы первой категории (требуют разрешение на привлечение)
		// - parentId = 5: дочерние элементы второй категории (не требуют разрешения)
		useEffect(() => {
			if (values.foreignLaborInfo.dnonResidentAttractingBasisCode) {
				// Фильтруем разрешения по типу резидентства (TI или IRS)
				const permissionList =
					irsPermissionList?.length > 0 ? irsPermissionList?.filter((v) => v.residentType === residentType) : []
				setFilteredNumberPermissionsList(permissionList)

				// Находим выбранное основание привлечения
 			const basis = nonresidentAttractingBasis?.find(
 				(item) => item.code === values.foreignLaborInfo.dnonResidentAttractingBasisCode
 			)

				// Проверяем требуется ли разрешение (parentId = 1) и есть ли доступные разрешения
				if (basis?.parentId === 1 && Boolean(permissionList?.length < 1)) {
					setIsPermitError(`required_permit_${residentType.toLowerCase()}`)
				}

				// Если разрешение не требуется, очищаем поля номера и даты разрешения
				if (basis?.parentId !== 1) {
					setFieldValue('foreignLaborInfo.nonResidentPermitNumber', null)
					setFieldValue('foreignLaborInfo.nonResidentPermitDate', null)
					setFieldValue('foreignLaborInfo.employeeFio', null)
				}

				// Устанавливаем флаг необходимости разрешения
				setWithPermission(basis?.parentId === 1)

				// Управляем отображением ошибок
				if (basis?.parentId !== 1) setIsPermitError('')
				if (basis?.parentId && !Boolean(permissionList?.length < 1)) {
					setIsPermitError('')
				}
			}
		}, [values.foreignLaborInfo.dnonResidentAttractingBasisCode, irsPermissionList])

		// Автоматическое определение срока действия трудового договора в зависимости от типа иммигранта
		// TI (трудовой иммигрант) - 9 месяцев, остальные типы - 7 месяцев
		useEffect(() => {
			if (immigrantType !== 'TI') {
				setFieldValue(formFieldNames.validityTerm, '7')
			}
			if (immigrantType === 'TI') {
				setFieldValue(formFieldNames.validityTerm, '9')
			}
		}, [immigrantType])

		// Обработка изменений даты разрешения и флага миграции
		// Включает перезагрузку календаря при изменении статуса миграции
		useEffect(() => {
			if (values.foreignLaborInfo.isMigration === 1) {
				// Перезагрузка календаря для корректного отображения
				setLoadCalendar(false)
				setTimeout(() => {
					setLoadCalendar(true)
				}, 100)
			}
			// Если дата разрешения не установлена, показываем все разрешения для данного типа резидентства
			if (!values.foreignLaborInfo.nonResidentPermitDate)
				setFilteredNumberPermissionsList(irsPermissionList?.filter((v) => v.residentType === residentType))
		}, [values.foreignLaborInfo.nonResidentPermitDate, values.foreignLaborInfo.isMigration])

		// Обработчик выбора номера разрешения из выпадающего списка
		// Автоматически заполняет связанные поля (дата, сроки действия, тип резидентства)
		const handlePermitNumberChange = (selectedPermitNumber) => {
			const selectedPermit = irsPermissionList?.find((item) => item.permitCode === selectedPermitNumber)
			if (selectedPermit) {
				// Заполняем все поля на основе выбранного разрешения
				setFieldValue('foreignLaborInfo.nonResidentPermitNumber', selectedPermitNumber)
				setFieldValue('foreignLaborInfo.nonResidentPermitDate', formatDate(selectedPermit?.permitIssueDate))
				setFieldValue('foreignLaborInfo.nonresidentBeginDate', selectedPermit?.permitBeginDate)
				setFieldValue('foreignLaborInfo.nonresidentEndDate', selectedPermit?.permitEndDate)
				setFieldValue('foreignLaborInfo.residentType', selectedPermit?.residentType)
				setFieldValue('foreignLaborInfo.employeeFio', selectedPermit?.employeeFio)

				// Устанавливаем даты договора только для определенных случаев
				if ((isEdit && !eContract) || !isEdit) {
					setFieldValue('dateFrom', formatDate(selectedPermit?.permitBeginDate))
					setFieldValue('dateTo', formatDate(selectedPermit?.permitEndDate))
				}
			} else {
				// Очищаем все поля и восстанавливаем полный список разрешений
				setFilteredNumberPermissionsList(irsPermissionList?.filter((v) => v.residentType === residentType))
				setFieldValue('foreignLaborInfo.nonResidentPermitNumber', '')
				setFieldValue('foreignLaborInfo.nonResidentPermitDate', '')
				setFieldValue('foreignLaborInfo.nonresidentBeginDate', '')
				setFieldValue('foreignLaborInfo.nonresidentEndDate', '')
				setFieldValue('foreignLaborInfo.residentType', '')
				setFieldValue('foreignLaborInfo.employeeFio', '')
			}
		}

		// Обработчик выбора даты разрешения через календарь
		// Фильтрует доступные номера разрешений по выбранной дате
		const handlePermitDateChange = (date, field) => {
			const formattedDate = formatDate(date)
			const selectedPermit = irsPermissionList.filter((item) => item.permitIssueDate === formattedDate)

			if (date) {
				setFieldValue('foreignLaborInfo.nonResidentPermitDate', formatDate(date))
				// Показываем только разрешения с выбранной датой
				setFilteredNumberPermissionsList(selectedPermit)
			} else {
				// Очищаем связанные поля при сбросе даты
				setFieldValue('foreignLaborInfo.nonResidentPermitDate', '')
				setFieldValue('foreignLaborInfo.nonResidentPermitNumber', '')
				setFieldValue('foreignLaborInfo.employeeFio', '')
			}
		}

		return (
			<>
				{/* Поле выбора основания привлечения нерезидентов
				    Использует иерархический справочник (selectTree) с условной логикой:
				    - При выборе элемента с parentId = 1 активируются поля разрешений
				    - При выборе элемента с parentId = 5 поля разрешений отключаются */}
				<FormikField name={'foreignLaborInfo.dnonResidentAttractingBasisCode'}>
					{({ field, meta: { touched, error } }) => (
						<Field
							type="text"
							fieldType="selectTree"
							label={t('contractDuration.base')}
							isRequired
							placeholder={t('choose_from_handbook')}
							options={nonresidentAttractingBasis}
							error={(touched || isFormDirty) && error}
							hint={t('contractDuration.hint.base')}
							{...field}
						/>
					)}
				</FormikField>

				{/* Отображение ошибки если требуется разрешение, но оно недоступно */}
				{isPermitError && <Error classNameError={styles.errorIsPermit}>{t(`validation.${isPermitError}`)}</Error>}

				{/* Блок полей разрешений (номер и дата) - активируется условно */}
				<div className={`${styles.fields} ${styles.threeFields}`}>
					{/* Поле выбора номера разрешения с поиском
					    Автоматически заполняет связанные поля при выборе */}
					<FormikField name={'foreignLaborInfo.nonResidentPermitNumber'}>
						{({ field, meta: { touched, error } }) => {
							return (
								<Field
									type="text"
									hideNotFoundError={Boolean(error)}
									onCustomChange={handlePermitNumberChange}
									fieldType="selectSearch"
									label={t('contractDuration.permit_number')}
									placeholder={t('contractDuration.permit_number_placeholder')}
									isRequired={withPermission}
									{...field}
									options={filteredNumberPermissionsList}
									hint={t('contractDuration.hint.permit_number')}
									disabled={!withPermission}
									error={(touched || isFormDirty) && error}
								/>
							)
						}}
					</FormikField>

					<FormikField name={'foreignLaborInfo.employeeFio'}>
						{({ field, meta: { touched, error } }) => {
							return (
								<Field
									type="text"
									label={t('contractDuration.employeeFio')}
									{...field}
									disabled={true}
									error={(touched || isFormDirty) && error}
								/>
							)
						}}
					</FormikField>

					{/* Поле выбора даты разрешения
					    Фильтрует доступные номера разрешений по выбранной дате */}
					{loadCalendar ? (
						<FormikField name={'foreignLaborInfo.nonResidentPermitDate'}>
							{({ field, meta: { touched, error } }) => (
								<Field
									fieldType="datePicker"
									onCustomChange={handlePermitDateChange}
									label={t('contractDuration.dateOfPermit')}
									isRequired={withPermission}
									placeholder={t('default_date_value')}
									error={(touched || isFormDirty) && error}
									hint={t('contractDuration.hint.dateOfPermit')}
									disabled={!withPermission}
									{...field}
								/>
							)}
						</FormikField>
					) : null}
				</div>

				{/* Трёхуровневая система образования нерезидентов */}

				{/* Первый уровень - Образование (родительские элементы с parentId = null) */}
				<FormikField name={'foreignLaborInfo.codes.value1'}>
					{({ field, meta: { touched, error } }) => (
						<Field
							type="text"
							fieldType="select"
							label={t('contractDuration.education')}
							isRequired
							placeholder={t('choose_from_handbook')}
							options={nonresidentEducation.filter((item) => item.parentId === null)}
							error={(touched || isFormDirty) && error}
							hint={t('contractDuration.hint.education')}
							{...field}
						/>
					)}
				</FormikField>

				{/* Второй уровень - Специальность (дочерние элементы выбранного образования) */}
				<FormikField name={'foreignLaborInfo.codes.value2'}>
					{({ field, meta: { touched, error } }) => (
						<Field
							type="text"
							fieldType="select"
							label={t('contractDuration.speciality')}
							isRequired={childsValue1.length}
							placeholder={t('choose_from_handbook')}
							options={childsValue1}
							error={(touched || isFormDirty) && error}
							disabled={!values.foreignLaborInfo.codes.value1 || !childsValue1.length}
							hint={t('contractDuration.hint.speciality')}
							{...field}
						/>
					)}
				</FormikField>

				{/* Третий уровень - Квалификация (дочерние элементы выбранной специальности) */}
				<FormikField name={'foreignLaborInfo.codes.value3'}>
					{({ field, meta: { touched, error } }) => (
						<Field
							type="text"
							fieldType="select"
							label={t('contractDuration.qualification')}
							isRequired={childsValue2.length}
							placeholder={t('choose_from_handbook')}
							options={childsValue2}
							error={(touched || isFormDirty) && error}
							disabled={!values.foreignLaborInfo.codes.value2 || !childsValue2.length}
							hint={t('contractDuration.hint.qualification')}
							{...field}
						/>
					)}
				</FormikField>
			</>
		)
	}
)
